import {
  PANDECTES_RUNTIME_EVENT_NAME,
  PANDECTES_ANALYTICS_EVENT_NAME,
  PANDECTES_WINDOW_QUEUE_KEY,
  PANDECTES_WINDOW_LATEST_DETAIL_KEY,
  normalizePandectesConsent,
} from './pandectes-consent.js';
import {
  buildProductAddedToCartPayload,
  calculateDiscount,
  collectLineItems,
  collectProductCards,
  flushBrazeBeforeNavigation,
  getActiveCurrencyOrDefault,
  getLocaleData,
  logBrazeCustomEvent,
  normalizeEmailForHash,
  parseCentsToMajorUnits,
  sha256Hex,
} from './braze-events.js';

/**
 * Custom Analytics Tracking - Shopify Custom Pixel Approach
 * Publishes events via Shopify.analytics.publish to be consumed by Custom Pixel
 */

// Initialize dataLayer on main window (required for Custom Pixel to push events)
window.dataLayer = window.dataLayer || [];

// Listen for analytics events from Custom Pixel (via postMessage)
window.addEventListener('message', function (event) {
  if (event.data && event.data.type === 'ARDENE_ANALYTICS_EVENT') {
    window.dataLayer.push(event.data.payload);
  }
});

/**
 * Utilities
 */
function publishAnalytics(eventName, payload) {
  if (!window.Shopify?.analytics?.publish) {
    console.error('Shopify.analytics.publish not available');
    return false;
  }
  window.Shopify.analytics.publish(eventName, payload);
  return true;
}

let lastPublishedPandectesConsentSignature = '';

function publishPandectesConsent(detail) {
  const payload = normalizePandectesConsent(detail);
  if (!payload) return false;

  const payloadSignature = JSON.stringify(payload);
  if (payloadSignature === lastPublishedPandectesConsentSignature) {
    return false;
  }

  const didPublish = publishAnalytics(PANDECTES_ANALYTICS_EVENT_NAME, payload);

  if (didPublish) {
    lastPublishedPandectesConsentSignature = payloadSignature;
  }

  return didPublish;
}

function flushPendingPandectesConsent() {
  const queuedDetails = Array.isArray(window[PANDECTES_WINDOW_QUEUE_KEY])
    ? [...window[PANDECTES_WINDOW_QUEUE_KEY]]
    : [];

  window[PANDECTES_WINDOW_QUEUE_KEY] = [];

  if (queuedDetails.length > 0) {
    queuedDetails.forEach((detail) => {
      publishPandectesConsent(detail);
    });
    return;
  }

  if (window[PANDECTES_WINDOW_LATEST_DETAIL_KEY]) {
    publishPandectesConsent(window[PANDECTES_WINDOW_LATEST_DETAIL_KEY]);
  }
}

function initPandectesConsentTracking() {
  window[PANDECTES_WINDOW_QUEUE_KEY] = Array.isArray(window[PANDECTES_WINDOW_QUEUE_KEY])
    ? window[PANDECTES_WINDOW_QUEUE_KEY]
    : [];

  window.addEventListener(PANDECTES_RUNTIME_EVENT_NAME, function (event) {
    publishPandectesConsent(event?.detail);
  });

  flushPendingPandectesConsent();
}


/** SHA-256 hex of logged-in customer email for pixel payloads only (no plain email on the wire). */
async function getCustomerEmailSha256ForPixel() {
  const raw = window.ardeneCustomerEmail;
  if (!raw || typeof raw !== 'string') return null;
  const normalized = normalizeEmailForHash(raw);
  if (!normalized) return null;
  try {
    return await sha256Hex(normalized);
  } catch (e) {
    console.error('[Analytics] sha256 customer email failed:', e);
    return null;
  }
}


/**
 * Dispatch analytics event when product is selected
 * @private
 * @param {CustomEvent} e - Event containing product details
 */
function handleSelectItem(e) {
  const { product } = e.detail;
  if (!product) return;

  try {
    if (!window.Shopify?.analytics?.publish) {
      console.error('Shopify.analytics.publish not available');
      return;
    }

    const variant =
      product.selected_variant ||
      product.variants?.find((v) => v?.available) ||
      product.variants?.[0] ||
      {};

    const price = parseCentsToMajorUnits(variant.price ?? product.price);
    const compareAtPrice =
      variant.compare_at_price != null
        ? parseCentsToMajorUnits(variant.compare_at_price)
        : null;

    const discount = calculateDiscount(price, compareAtPrice);
    const item = {
      item_id: product.ardeneStyle?.toString() || '',
      item_sku: variant.sku || '',
      item_name: product.title || '',
      item_brand: 'Ardene',
      price,
      quantity: 1,
      discount,
      internalCategory: product.internalCategory || '',
    };

    if (compareAtPrice != null) {
      item.compareAtPrice = compareAtPrice;
    }

    const currency = window.Shopify?.currency?.active || 'CAD';
    const payload = {
      item,
      listId: window.collection?.handle,
      listName: window.collection?.title,
      currency,
    };

    window.Shopify.analytics.publish('ardene:select_item', payload);
    logBrazeCustomEvent('select_item', payload);
  } catch (error) {
    console.error('Error dispatching analytics event:', error);
  }
}
function handleProductChange(e) {
  const { variant, product } = e.detail;

  try {
    // Publish through Shopify's analytics API
    if (window.Shopify?.analytics?.publish) {
      const currencyCode = window.Shopify?.currency?.active || 'CAD';
      let priceAmount;
      if (variant?.price != null) {
        priceAmount = parseCentsToMajorUnits(variant.price);
      } else if (product?.price != null) {
        priceAmount = parseCentsToMajorUnits(product.price);
      } else {
        priceAmount = 0;
      }

      const compareAtPriceAmount = variant?.compare_at_price != null
        ? parseCentsToMajorUnits(variant.compare_at_price)
        : undefined;
      const discount = calculateDiscount(priceAmount, compareAtPriceAmount);

      const rootUrl = window.Shopify?.routes?.root || window.routes?.root_url || '/';
      const imageSrc =
        variant?.featured_image?.src ||
        variant?.image?.src ||
        variant?.image ||
        product?.featured_image ||
        (Array.isArray(product?.images) && product.images.length > 0 ? product.images[0]?.src : '') ||
        '';

      const payload = {
        productVariant: {
          price: {
            amount: priceAmount || 0,
            currencyCode,
          },
          compareAtPrice: compareAtPriceAmount != null
            ? { amount: compareAtPriceAmount, currencyCode }
            : undefined,
          discount,
          product: {
            title: product?.title || '',
            vendor: product?.vendor || 'Ardene',
            id: (product?.id != null ? String(product.id) : undefined),
            untranslatedTitle: product?.title || '',
            url: product?.handle ? `${rootUrl}products/${product.handle}` : undefined,
            productType: product?.type || '',
            internalCategory: product?.internalCategory || '',
            ardeneStyle: product?.ardeneStyle || '',
            complexId: product?.complexId || ''
          },
          id: (variant?.id != null ? String(variant.id) : undefined),
          image: {
            src: imageSrc,
          },
          sku: variant?.sku || product?.productSku || '',
          title: variant?.title || '',
          untranslatedTitle: variant?.title || '',
        },
      };

      window.Shopify.analytics.publish('ardene:product_viewed', payload);
      logBrazeCustomEvent('product_viewed', payload);
    } else {
      console.error('Shopify.analytics.publish not available');
    }
  } catch (error) {
    console.error('Error dispatching analytics event:', error);
  }
}
function handleCartAdd(e) {
  const { variant, product, quantity } = e.detail || {};

  try {
    if (!window.Shopify?.analytics?.publish) {
      console.error('Shopify.analytics.publish not available');
      return;
    }

    const currencyCode = window.Shopify?.currency?.active || 'CAD';
    const payload = buildProductAddedToCartPayload({
      variant,
      product,
      quantity,
      currencyCode,
    });

    window.Shopify.analytics.publish('ardene:product_added_to_cart', payload);
  } catch (error) {
    console.error('Error dispatching analytics event:', error);
  }
}
function handleAddToWishlist(e) {
  const { variant, product } = e.detail;

  try {
    // Publish through Shopify's analytics API
    if (window.Shopify?.analytics?.publish) {
      const currencyCode = window.Shopify?.currency?.active || 'CAD';
      let priceAmount;
      if (variant?.price != null) {
        priceAmount = parseCentsToMajorUnits(variant.price);
      } else if (product?.price != null) {
        priceAmount = parseCentsToMajorUnits(product.price);
      } else {
        priceAmount = undefined;
      }

      const compareAtPriceAmount = variant?.compare_at_price != null
        ? parseCentsToMajorUnits(variant.compare_at_price)
        : undefined;
      const discount = calculateDiscount(priceAmount, compareAtPriceAmount);

      const rootUrl = window.Shopify?.routes?.root || window.routes?.root_url || '/';
      const imageSrc =
        variant?.featured_image?.src ||
        variant?.image?.src ||
        variant?.image ||
        product?.featured_image ||
        (Array.isArray(product?.images) && product.images.length > 0 ? product.images[0]?.src : '') ||
        '';

      const payload = {
        productVariant: {
          price: {
            amount: priceAmount,
            currencyCode,
          },
          compareAtPrice: compareAtPriceAmount != null
            ? { amount: compareAtPriceAmount, currencyCode }
            : undefined,
          discount,
          product: {
            title: product?.title || '',
            vendor: product?.vendor || 'Ardene',
            id: (product?.id != null ? String(product.id) : undefined),
            untranslatedTitle: product?.title || '',
            url: product?.handle ? `${rootUrl}products/${product.handle}` : undefined,
            productType: product?.type || '',
            internalCategory: product?.internalCategory || '',
            ardeneStyle: product?.ardeneStyle || '',
            complexId: product?.complexId || ''
          },
          id: (variant?.id != null ? String(variant.id) : undefined),
          image: {
            src: imageSrc,
          },
          sku: variant?.sku || product?.productSku || '',
          title: variant?.title || '',
          untranslatedTitle: variant?.title || '',
        },
      };

      window.Shopify.analytics.publish('ardene:add_to_wishlist', payload);
      logBrazeCustomEvent('add_to_wishlist', payload);
    } else {
      console.error('Shopify.analytics.publish not available');
    }
  } catch (error) {
    console.error('Error dispatching analytics event:', error);
  }
}

function handleProductRemovedFromCart(e) {
  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) return;

    const item = e.detail?.item;
    if (!item) return;

    const currency = getActiveCurrencyOrDefault();
    const quantity = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const total = (quantity * price).toFixed(2);

    // Shape a cartLine-like object for the custom pixel subscriber
    const cartLine = {
      merchandise: {
        id: item.item_sku || item.key || '',
        sku: item.item_sku || '',
        product: {
          id: item.item_id != null ? String(item.item_id) : '',
          title: item.item_name || '',
          productType: item.internalCategory || '',
          ardeneStyle: item.item_id || '',
          internalCategory: item.internalCategory || '',
        },
      },
      cost: {
        totalAmount: {
          amount: total,
          currencyCode: currency,
        },
      },
      compareAtPrice: item.compareAtPrice != null
        ? { amount: String(item.compareAtPrice), currencyCode: currency }
        : undefined,
      discount: item.discount || 0,
      quantity,
    };

    publishAnalytics('ardene:product_removed_from_cart', {
      cartLine,
    });
  } catch (error) {
    console.error('Error dispatching product_removed_from_cart analytics event:', error);
  }
}

function handleCartViewed() {
  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) return;

    const rootUrl = window.Shopify?.routes?.root || window.routes?.root_url || '/';
    const cartUrl = window.Shopify?.routes?.cart_url || window.routes?.cart_url || `${rootUrl}cart`;

    fetch(`${cartUrl}.js`, { headers: { 'Content-Type': 'application/json' } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`Failed to fetch cart: ${res.status}`))))
      .then((cart) => {
        const currency = cart?.currency || getActiveCurrencyOrDefault();
        const value = typeof cart?.total_price === 'number' ? cart.total_price / 100 : undefined;

        const items = collectLineItems();

        const payload = { cartData: { items }, currency, value };

        publishAnalytics('ardene:view_cart', payload);
        logBrazeCustomEvent('view_cart', payload);
      })
      .catch((err) => {
        console.error('Error fetching cart for view_cart:', err);
      });
  } catch (error) {
    console.error('Error dispatching view_cart analytics event:', error);
  }
}

function handleProductGridViewed(e) {
  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) return;

    // Support both event objects (e.detail?.grid) and direct DOM element calls
    const grid = e?.detail?.grid || (e?.querySelectorAll ? e : document);
    const listId = window.collection?.id || '';
    const listName = window.collection?.title || '';

    const products = collectProductCards(grid);
    if (!products.length) return;

    const currency = window.Shopify?.currency?.active || 'CAD';
    const payload = { listId, listName, products, currency };

    window.Shopify.analytics.publish('ardene:view_item_list', payload);
    logBrazeCustomEvent('view_item_list', payload);
  } catch (error) {
    console.error('Error dispatching view_item_list analytics event:', error);
  }
}

function handleBeginCheckoutClick(e) {
  const trigger = e.target.closest('[data-cart-submit]');
  if (!trigger) return;

  // Only handle the minicart checkout link (anchor with href to /checkout)
  const isInMinicart = Boolean(trigger.closest('#minicart, minicart-component'));
  const isLink = trigger.tagName === 'A';
  const href = isLink ? trigger.getAttribute('href') : null;
  if (!isInMinicart || !isLink || !href || !href.includes('/checkout')) return;

  // Prevent default navigation while we build/send analytics
  e.preventDefault();
  if (trigger.getAttribute('data-processing') === 'true') return;
  trigger.setAttribute('data-processing', 'true');
  trigger.setAttribute('aria-disabled', 'true');

  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) {
      window.location.assign(href);
      return;
    }

    // Build items array from DOM [data-line-item] entries in the minicart
    const items = collectLineItems();

    const currency = getActiveCurrencyOrDefault();
    const value = items.reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);

    const payload = { cartData: { items }, currency, value };

    publishAnalytics('ardene:begin_checkout', payload);
    logBrazeCustomEvent('begin_checkout', payload);

    // This handler navigates away. Drain the readiness queue and force the
    // SDK to transmit — the 100ms below is not long enough for Braze's own
    // request batching to elapse, so without this the event can be lost.
    flushBrazeBeforeNavigation();

    // Allow a short delay to ensure any subscribers process synchronously
    setTimeout(() => {
      window.location.assign(href);
    }, 100);
  } catch (error) {
    console.error('Error dispatching begin_checkout analytics event:', error);
    // Fail-safe: still proceed to checkout
    window.location.assign(href);
  }
}

/**
 * Initialize analytics tracking
 */
function initAnalytics() {
  document.addEventListener('select-item', handleSelectItem);
  document.addEventListener('product-change', handleProductChange);
  document.addEventListener('product-viewed', handleProductChange);
  document.addEventListener('product-added-to-cart', handleCartAdd);
  document.addEventListener('add-to-wishlist', handleAddToWishlist);
  document.addEventListener('cart-viewed', handleCartViewed);
  document.addEventListener('begin-checkout', handleBeginCheckoutClick);
  document.addEventListener('product-grid-ready', handleProductGridViewed);
  document.addEventListener('product-removed-from-cart', handleProductRemovedFromCart);
}
/** Publishes hashed email and locale for the custom pixel (each full page load). */
async function publishCustomerContextForPixel() {
  try {
    if (!window.Shopify?.analytics?.publish) return;
    const customerEmailSha256 = await getCustomerEmailSha256ForPixel();
    const localeData = getLocaleData();
    publishAnalytics(
      'ardene:customer_context',
      customerEmailSha256 ? { email: customerEmailSha256, ...localeData } : localeData
    );
  } catch (error) {
    console.error('Error publishing ardene:customer_context analytics event:', error);
  }
}

async function maybePublishCustomerLoggedIn() {
  try {
    const canPublish = Boolean(window.Shopify?.analytics?.publish);
    if (!canPublish) return;
    if (!window.sessionStorage) return;

    const alreadyFired = window.sessionStorage.getItem('customerLoginEventFired') === 'true';
    if (!window.customer) {
      window.sessionStorage.setItem('customerLoginEventFired', 'false');
      return;
    }
    if (alreadyFired) return;
    const customerEmailSha256 = await getCustomerEmailSha256ForPixel();
    if (!customerEmailSha256) return;
    const localeData = getLocaleData();
    publishAnalytics('ardene:customer_logged_in', { email: customerEmailSha256, ...localeData });
    window.sessionStorage.setItem('customerLoginEventFired', 'true');
  } catch (error) {
    console.error('Error dispatching ardene:customer_logged_in analytics event:', error);
  }
}
const productViewed = (productFormEl) => {
  const product = productFormEl.parseJsonAttribute('data-product');
  const ardeneStyle = productFormEl.getAttribute('data-product-style');
  const sku = productFormEl.getAttribute('data-product-sku');
  const internalCategory = productFormEl.getAttribute('data-internal-category');
  const complexId = productFormEl.getAttribute('data-complex-id');
  // Extend product object with additional attributes for unified access
  if (product) {
    const extendedProduct = {
      ...product,
      ardeneStyle: ardeneStyle,
      internalCategory: internalCategory,
      complexId: complexId,
      productSku: sku,
    };
    handleProductChange({ detail: { product: extendedProduct, variant: null } });
  }
  return null;
}

initPandectesConsentTracking();

// Register listeners at module load so a fast add-to-cart is not missed while
// customer-context publishing awaits hashing / Shopify.analytics readiness.
initAnalytics();

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  void (async () => {
    await publishCustomerContextForPixel();
    const productFormEl = document.querySelector('.js-product-page product-form');
    if (productFormEl) {
      productViewed(productFormEl);
    }
    const productGridEl = document.querySelector('product-grid');
    if (productGridEl) {
      handleProductGridViewed(productGridEl);
    }
    await maybePublishCustomerLoggedIn();
  })();
});
